import React, { useEffect, useState, useRef, useContext } from "react";
import "./style.css";
import { Button, Select, Switch } from "antd";
import html2canvas from "html2canvas";
import fullDark from "../../Assets/icons/FullScreenDark.svg";
import fullLight from "../../Assets/icons/FullScreenLight.svg";
import downloadDark from "../../Assets/icons/DownloadDark.svg";
import downloadLight from "../../Assets/icons/DownloadLight.svg";
import { ThemeContext } from "../../Contexts/ThemeProvider";
import GenericTable from "../../Components/GenericTable";
import menuIcon from "../../Assets/icons/Sidebaricon/panel-right-close.svg";

const GraphLayout = ({
  children,
  title,
  data,
  setFilteredLabels,
  setSeries,
  selectTenByDefault = false,
  colourTheme,
  classNameCSS,
  showSort = true,
}) => {
  if (data?.label) {
    data.labels = data.label;
    delete data.label;
  }
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  const exportRef = useRef();
  const labels = data?.labels || [];
  const defaultCount = selectTenByDefault ? 6 : 3;
  const initialSelection = labels.slice(0, defaultCount);
  const [selected, setSelected] = useState(initialSelection);
  const allOptionValue = "__ALL__";
  const allSelected = labels.length > 0 && selected.length === labels.length;
  const seriesKeys = Object.keys(data).filter((key) => key !== "labels");
  const options = [
    {
      label: allSelected ? "Unselect All" : "Select All",
      value: allOptionValue,
    },
    ...labels.map((label) => ({
      label,
      value: label,
    })),
  ];
  const [sortConfig, setSortConfig] = useState({});
  const [showFullScreen, setShowFullScreen] = useState(false);

  useEffect(() => {
    const existingStyle = document.getElementById(
      `dynamic-button-styles-${classNameCSS}`
    );
    if (existingStyle) {
      document.head.removeChild(existingStyle);
    }

    const style = document.createElement("style");
    style.id = `dynamic-button-styles-${classNameCSS}`;
    let css = "";

    colourTheme.forEach((color, index) => {
      const hoverColor = colourTheme[index];

      document.documentElement.style.setProperty(
        `--${classNameCSS}-btn-color${index}`,
        color
      );
      document.documentElement.style.setProperty(
        `--${classNameCSS}-btn-hover-color${index}`,
        hoverColor
      );

      css += `
      .${classNameCSS} .dynamic-button${index} span {
        color: var(--${classNameCSS}-btn-color${index}) !important;
        border-color: var(--${classNameCSS}-btn-color${index}) !important;
        transition: all 0.3s;
      }
      .${classNameCSS} .dynamic-button${index}:hover {
        color: var(--${classNameCSS}-btn-hover-color${index}) !important;
        border-color: var(--${classNameCSS}-btn-hover-color${index}) !important;
      }
    `;
    });

    style.innerHTML = css;
    document.head.appendChild(style);

    return () => {
      if (style && document.head.contains(style)) {
        document.head.removeChild(style);
      }
      colourTheme.forEach((_, index) => {
        document.documentElement.style.removeProperty(
          `--${classNameCSS}-btn-color${index}`
        );
        document.documentElement.style.removeProperty(
          `--${classNameCSS}-btn-hover-color${index}`
        );
      });
    };
  }, [colourTheme, classNameCSS]);

  useEffect(() => {
    if (!labels.length) return;

    const selectedIndexes = labels
      .map((label, index) => (initialSelection.includes(label) ? index : -1))
      .filter((index) => index !== -1);

    const dynamicSeries = seriesKeys.map((key) => ({
      name: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      data: selectedIndexes.map((i) => data[key][i] ?? 0),
    }));

    setFilteredLabels(initialSelection);
    setSeries(dynamicSeries);
  }, [data]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setShowFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  const handleSort = (key) => {
    let direction;
    if (!sortConfig[key]) {
      direction = "asc";
    } else if (sortConfig[key] === "asc") {
      direction = "desc";
    } else {
      direction = null;
    }

    const newSortConfig = { ...sortConfig, [key]: direction };
    setSortConfig(newSortConfig);

    if (direction) {
      const sortedData = sortData(selected, data, key, direction);
      setFilteredLabels(sortedData.sortedLabels);
      setSeries(sortedData.sortedSeries);
    } else {
      // Reset to original order
      const selectedIndexes = labels
        .map((label, index) => (selected.includes(label) ? index : -1))
        .filter((index) => index !== -1);

      const dynamicSeries = seriesKeys.map((seriesKey) => ({
        name: seriesKey,
        data: selectedIndexes.map((i) => data[seriesKey][i] ?? 0),
      }));

      setFilteredLabels(selected);
      setSeries(dynamicSeries);
    }
  };

  const sortData = (selectedLabels, data, sortKey, direction) => {
    const labelValuePairs = selectedLabels.map((label, index) => ({
      label,
      value: data[sortKey][labels.indexOf(label)],
      originalIndex: index,
    }));

    labelValuePairs.sort((a, b) => {
      if (a.value === b.value) return 0;
      if (direction === "asc") {
        return a.value < b.value ? -1 : 1;
      } else {
        return a.value > b.value ? -1 : 1;
      }
    });

    const sortedLabels = labelValuePairs.map((item) => item.label);

    const sortedSeries = seriesKeys.map((key) => {
      const originalIndices = labelValuePairs.map((item) =>
        labels.indexOf(item.label)
      );
      return {
        name: key,
        data: originalIndices.map((i) => data[key][i] ?? 0),
      };
    });

    return { sortedLabels, sortedSeries };
  };

  const handleDownload = async () => {
    if (!exportRef.current) return;

    const elementsToHide =
      exportRef.current.querySelectorAll(".hide-on-export");

    const originalDisplay = [];
    elementsToHide.forEach((el, i) => {
      originalDisplay[i] = el.style.display;
      el.style.display = "none";
    });

    const canvas = await html2canvas(exportRef.current, {
      useCORS: true,
      backgroundColor: null,
      scale: 2,
    });

    elementsToHide.forEach((el, i) => {
      el.style.display = originalDisplay[i];
    });

    const link = document.createElement("a");
    link.download = `${title || "graph"}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const handleChange = (value) => {
    let newValue;

    if (value.includes(allOptionValue)) {
      newValue = allSelected ? [] : [...labels];
    } else {
      newValue = value;
    }

    setSelected(newValue);
    setFilteredLabels(newValue);

    const selectedIndexes = labels
      .map((label, index) => (newValue.includes(label) ? index : -1))
      .filter((index) => index !== -1);

    const dynamicSeries = seriesKeys.map((key) => ({
      name: key,
      data: selectedIndexes.map((i) => data[key][i] ?? 0),
    }));

    setSeries(dynamicSeries);
  };

  const getSortIcon = (key) => {
    if (!sortConfig[key]) return "⇅";
    return sortConfig[key] === "asc" ? "↑" : "↓";
  };

  const capitalizeFirst = (str) => {
    const formatted = str.replace(/_/g, " ");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const handleToggleFullScreen = () => {
    if (!document.fullscreenElement) {
      exportRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const dataSource = labels.map((label, index) => {
    const row = { key: index, label };
    seriesKeys?.forEach((key) => {
      row[key] = data[key]?.[index] ?? 0;
    });
    return row;
  });

  const columns = [
    {
      title: "Vehicle Name",
      dataIndex: "label",
      key: "label",
      sorter: (a, b) =>
        a.label.localeCompare(b.label, undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    ...seriesKeys?.map((key) => ({
      title: key?.replace(/_/g, " ").replace(/\b\w/g, (c) => c?.toUpperCase()),
      dataIndex: key,
      key,
      sorter: (a, b) => (a[key] ?? 0) - (b[key] ?? 0),
    })),
  ];

  return (
    <div
      ref={exportRef}
      className={`${showFullScreen ? "graph-layout-fullscreen" : ""}`}
    >
      <div
        className={`${showFullScreen ? "graph-layout-fullscreen-wrapper" : ""}`}
      >
        {showFullScreen && (
          <div>
            <div className="header-layout">
              <div className="left">
                <div className="panel-right">
                  <img
                    className="panel-icon"
                    src={menuIcon}
                    alt="Toggle Sidebar"
                  />
                </div>

                <div className="vertical-line"></div>
                <div className="page-title">{title}</div>
              </div>
              <div className="theme-toggle-container">
                <span className="theme-label">
                  {isDarkMode ? "Dark" : "Light"}
                </span>
                <Switch checked={isDarkMode} onChange={toggleTheme} />
              </div>
            </div>
            <div className="horizontal-line" />
          </div>
        )}
        <div className={`${showFullScreen ? "fullscreen-padding" : ""}`}>
          <div className="graph-layout">
            <div className="graph-title">{title}</div>
            <div className="actions">
              <Select
                popupClassName="custom-select-dropdown"
                className="hide-on-export"
                mode="multiple"
                style={{ width: "100%" }}
                options={options}
                placeholder="Select Vehicle/s to Display..."
                maxTagCount="responsive"
                value={selected}
                onChange={handleChange}
                getPopupContainer={(triggerNode) =>
                  exportRef.current || triggerNode.parentNode
                }
              />
              <div className={`right ${classNameCSS} hide-on-export`}>
                {showSort &&
                  seriesKeys.map((key, index) => (
                    <Button
                      className={`dynamic-button${index}`}
                      key={key}
                      onClick={() => handleSort(key)}
                      style={{ marginLeft: 8 }}
                    >
                      {capitalizeFirst(key)} {getSortIcon(key)}
                    </Button>
                  ))}

                <Button onClick={handleDownload}>
                  <img
                    src={isDarkMode ? downloadDark : downloadLight}
                    alt="download"
                  />
                </Button>
              </div>
            </div>
            <div className="chart-wrapper">{children}</div>
            <div className="full-screen hide-on-export">
              <span
                className="full-screen-icon"
                onClick={handleToggleFullScreen}
              >
                <img
                  src={isDarkMode ? fullDark : fullLight}
                  alt="full-screen"
                />
              </span>
            </div>
          </div>

          {showFullScreen && (
            <div style={{ marginTop: "20px" }}>
              <GenericTable
                title={title + " Table"}
                dataSource={dataSource}
                columns={columns}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraphLayout;
